import { useEffect, useState } from "react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "react-hot-toast";
import { CheckoutButton } from '../components/CheckoutButton';
import { supabase } from "@/integrations/supabase/client";

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price_month: number;
  price_year: number;
  stripe_price_id_month?: string;
  stripe_price_id_year?: string;
}

const BuyCredits = () => {
  const { credits, user } = useAuth();
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState<'month' | 'year'>('month');

  useEffect(() => {
    const fetchPacks = async () => {
      if (!user?.id) return;
      // Fetch credit packs
      const { data: packsData, error: packsError } = await supabase
        .from('credit_packs')
        .select('*')
        .order('credits');
      if (packsError) {
        toast.error('Failed to fetch credit packs');
      } else {
        setPacks((packsData || []).map((item: any) => ({
          id: item.id,
          name: item.name,
          credits: item.credits,
          price_month: item.price_month ?? 0,
          price_year: item.price_year ?? 0,
          stripe_price_id_month: item.stripe_price_id_month ?? '',
          stripe_price_id_year: item.stripe_price_id_year ?? '',
        })));
      }
      setLoading(false);
    };
    fetchPacks();
  }, [user]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container py-8">
          <div className="text-center">Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8">
        <h1 className="text-3xl font-bold mb-2">Koupit kredity</h1>
        <p className="text-gray-500 mb-8">
          Aktuálně máte <span className="font-medium text-[#14A48D]">{credits} kreditů</span>.
          Nakupte další kredity pro pokračování ve využívání virtuálního zařízení.
        </p>
        <div className="flex items-center gap-4 mb-6">
          <button
            className={`px-4 py-2 rounded-lg border bg-blue-600 text-white`}
            onClick={() => setInterval('month')}
            disabled
          >
            Měsíční
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packs.length === 0 ? (
            <div className="col-span-3 text-center text-gray-500">Žádné balíčky kreditů nejsou k dispozici.</div>
          ) : (
            packs.map((pkg) => (
              <div
                key={pkg.id}
                className="border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <h2 className="text-2xl font-semibold mb-2">{pkg.credits} kreditů</h2>
                <p className="text-gray-600 mb-4">{pkg.price_month} Kč / měsíc ({(pkg.price_month / pkg.credits).toFixed(0)} Kč / 1 kredit)</p>
                <CheckoutButton
                  subscriptionType={'monthly'}
                  planId={pkg.id}
                  priceId={pkg.stripe_price_id_month || ''}
                  credits={pkg.credits}
                  userEmail={user?.email || ''}
                  interval={'month'}
                  currentCredits={credits}
                  className="w-full"
                />
              </div>
            ))
          )}
        </div>
        <div className="bg-gray-50 p-6 rounded-lg mb-8">
          <h2 className="text-xl font-semibold mb-4">Jak fungují kredity</h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="bg-[#14A48D] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">1</span>
              <p>Každý měsíc získáte novou dávku kreditů podle zvoleného tarifu.</p>
            </li>
            <li className="flex items-start">
              <span className="bg-[#14A48D] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">2</span>
              <p>Kredity slouží k objednání virtuálního stagingu (1 kredit = 1 staging).</p>
            </li>
            <li className="flex items-start">
              <span className="bg-[#14A48D] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">3</span>
              <p>Nevyužité kredity se na další měsíc nepřevádějí.</p>
            </li>
          </ul>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BuyCredits;
